package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/remember_updated_state")
@Composable
fun RememberUpdatedStatePage() {
    BlogLayout(
        PageProperties(
            "rememberUpdatedState",
            "rememberUpdatedState - (Always Use the Latest Callback - Preventing Unnecessary Effect Restarts - Prevents stale captures in long-lived effects long-running coroutines or callbacks - Non-Restarting References - Capture the latest value without restarting an effect - show or hide (dialog, compose function hide/show) or lambda inside the LaunchedEffect, WindowInsets"
        ),
        BlogProperties("Jetpack Compose", "rememberUpdatedState"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "The Problem: Stale Captured Values",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A LaunchedEffect(Unit) captures the references in scope when it first launches. If a referenced value changes during the effect’s execution, the running coroutine remains stuck reading the old, captured instance.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Restarting the effect by passing the changing variable as a key cancels the coroutine, which is often undesirable for ongoing timers, network calls, or animations.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "The Solution",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberUpdatedState wraps the value in a stable State reference that updates on every recomposition. This allows long-lived effects to read the freshest value at the exact moment of execution without triggering a coroutine restart.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "rememberUpdatedState - (Always Use the Latest Callback - Preventing Unnecessary Effect Restarts - Prevents stale captures in long-lived effects long-running coroutines or callbacks - Non-Restarting References - Capture the latest value without restarting an effect - show or hide (dialog, compose function hide/show) or lambda inside the LaunchedEffect, WindowInsets",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When an effect runs in the background and you need to use the latest version of a callback or lambda - this keeps it updated without restarting the effect.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember a mutableStateOf and update its value to newValue on each recomposition of the rememberUpdatedState call.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose provides some data structures to manage the dynamically changing state of applications, and one of these data structures is the rememberUpdatedState function. The function’s feature is to track a change count whenever any changes are made. This count is used to keep track of when the value was last changed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect is a composable function, so it can only be used inside other composable functions. If you want to start a coroutine outside of a composable but have it automatically cancelled when you leave the composition, use rememberCoroutineScope. You can also use rememberCoroutineScope whenever you need to manually control one or more coroutine lifecycles, such as canceling an animation when a user event occurs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberCoroutineScope is a composable function that returns a coroutine scope bound to the CoroutineScope of the calling Composition. The scope will be cancelled when you leave the Composition when you call it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Compose, LaunchedEffect typically restarts when one of its key parameters changes. However, there are situations where you want to reference a value that can change without restarting the effect. (LaunchedEffect restarts when its key parameters change. However, there are scenarios where you need to capture the latest state without restarting the effect. In such cases, rememberUpdatedState comes to the rescue.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is used when you want a stable reference to some changing state within a side effect. It ensures that the latest value is available for a side effect while keeping the side effect stable. (Use rememberUpdatedState when you need to reference the latest state in long-running side effects without triggering restarts.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberUpdatedState is used when we want to keep an updated reference to a variable in a long-running side-effect without having the side-effect restart on recomposition. (rememberUpdatedState is designed for this purpose it captures the latest value of a state within an effect, ensuring that long-lived operations remain efficient.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To capture a value in an effect & update it later without triggering it (rememberUpdatedState creates a reference to the value, captures the actual value, and when modified, the modified value is captured). Useful for long lived operations that would be costly to recreate and restart.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This approach is particularly useful when you have a callback or lambda function passed into a composable that may change over time. You may not want to restart the entire effect when the callback changes, especially if the effect is managing a complex operation like a long-running coroutine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "There are situations where you need to access the latest state of a Composable, even when it changes within a Composable function. When you want to compare and track changes in a Composable’s state between recompositions. When effect depends on a value that might change over time but we still want the effect to span across recompositions Every time it is called it will update its value but will not retrigger effects reading from it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Variables that need to be accessed in the internal block of LaunchedEffect must be passed as Key (param). LaunchedEffect‘s param cancels and restarts the corresponding coroutine when a change occurs. If you need to access inside the block of LaunchedEffect, but in order not to restart LaunchedEffect even if it is changed, the corresponding value must be wrapped once with rememberUpdatedState.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Often used when you have two tasks, one short and one long, and you want to avoid the long task from repeating every time the short task runs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This API is useful when effects need to keep up with long-awaited work, heavy operations, or to avoid being restarted by recomposition. When you have long-lived operations or expensive calculations that you don't want to recreate every time the composable is recomposed. For example, if you need to do something after waiting for a certain amount of time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberUpdatedState makes sure the MutableState is always updated with the latest value instead of caching the initial composition value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Timer: We have a timer that starts as soon as the composable is on the screen and completes after 5 seconds. Pay attention to the fact that we have used Unit (a never-changing constant value) as a parameter for key1 in LaunchedEffect so that this side- effect never restarts even if the parent composable is recomposed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "There are situations where you need to access the latest state of a Composable, even when it changes within a Composable function. This is where rememberUpdatedState comes into play. It ensures that you can access the most recent state of a composable within its scope, regardless of when that state is updated. This is particularly useful when working with mutable state that might change multiple times during composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Key Points:",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )

                        Ol {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "It prevents stale captures in long-lived effects and callbacks.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Ensure callbacks always use the latest lambda.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Timer or delay operations with dynamic callbacks.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Any effect where you need the latest lambda without restarting.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Avoid “stale” values in long-running coroutines.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "It does not trigger or control recomposition by itself; it just holds the latest value behind a stable reference.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Use it when you want an effect to keep running, but to act with the newest parameters without recreating the effect.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "A timer that should always use the latest onTick callback without restarting the coroutine each time the callback changes.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Useful for timer, pointerInput, LaunchedEffect, animation, long-lived lambdas, or event listeners.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }
                }

                KotlinCode(
                    code = """
                    //1    
                    @composable
                    fun MyComposableExample(timeOut: () -> Unit) {
                      val timer = rememberUpdatedState(0)

                      LaunchedEffect(timer) {
                        delay(5000)
                        timeOut()
                      }
                      Text(text = timer.value)
                    }
                    
                    //2
                    @Composable fun Ticker(
                        intervalMs: Long, 
                        onTick: (Long) -> Unit
                    ) {
                        //Keep a stable State whose value is always the latest onTick
                        val currentOnTick by rememberUpdatedState(onTick)
                        
                        LaunchedEffect intervalMs) {
                            var elapsed = OL
                            while(true) ( 
                                delay( intervalMs)
                                elapsed += intervalMs
                                
                                //Uses the latest callback, even if it changed after LaunchedEffect started
                                currentOnTick(elapsed)
                            )
                        }
                        
                    
                    }    
                    
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    🚫 Problem
                    // rememberUpdatedState is meant for capturing latest values inside side-effects (like LaunchedEffect, pointerInput, etc.).
                    
                    @Composable
                    fun ToggleButton() {
                        var isChecked by remember { mutableStateOf(false) }
                        //val updatedIsChecked by rememberUpdatedState(isChecked)
                        var updatedIsChecked by remember { mutableStateOf(isChecked) } 
                        
                        Button(
                            onClick = { isChecked = !isChecked }
                        ) {
                            Text(if (updatedIsChecked) "ON" else "OFF")
                        }
                    }
                    """.trimIndent()
                )

                //https://www.rikkaui.dev
                KotlinCode(
                    code = """
                    @Composable
                    fun Slider(
                        value: Float,
                        onValueChange: (Float) -> Unit,
                    ) {
                        val clampedValue = value.coerceIn(0f, 1f)
                        
                        // Keep latest callback stable for pointer input lambdas.
                        //rememberUpdatedState only inside event handlers
                        //rememberUpdatedState: pointerInput, LaunchedEffect, animations / long-lived lambdas
                        val currentOnValueChange by rememberUpdatedState(onValueChange)
                        
                        Modifier.pointerInput(Unit) {
                                detectHorizontalDragGestures { change, _ ->
                                    change.consume()
                                    val width =
                                        trackWidthPx.intValue.toFloat()
                                    if (width > 0f) {
                                        val newValue =
                                            (change.position.x / width)
                                                .coerceIn(0f, 1f)
                                        currentOnValueChange(newValue)
                                    }
                                }
                            }
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example, rememberUpdatedState is used to track the isChecked state. Even though isChecked is updated when the button is clicked, updatedIsChecked always reflects the latest state, ensuring the button text is accurate.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "RememberUpdatedState composable is used to create a state that is updated whenever the composable is recomposed. This means that you can use it to update the state of the value inside a LaunchedEffect composable during recomposition. Here There is some long task in launchedEffect. During recomposition, I don’t want that this task exectus again but I want uptodate value in the text, so rememebr is used. Here Log will always display up-to-date value even if it is in LaunchedEffect.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //old callback value
                    LaunchedEffect(Unit) {
                        delay(5000)
                        onTimeout() // Might be stale!
                    }

                    //latest callback
                    LaunchedEffect(Unit) {
                        val currentCallback by rememberUpdatedState(onTimeout)
                        delay(5000)
                        currentCallback() // Always fresh!
                    }
                    
                    //2 This one trips up a lot of developers. Imagine a splash screen with a 3-second delay. The onTimeout callback might change (because the parent recomposed) while the delay is in progress. If you reference onTimeout directly inside LaunchedEffect(Unit), you've captured a stale lambda.
                    @Composable
                    fun SplashScreen(onTimeout: () -> Unit) {
                        // Without this, onTimeout captured at launch time might be stale
                        val currentOnTimeout by rememberUpdatedState(onTimeout)
                       
                        LaunchedEffect(Unit) {
                            delay(3_000)
                            currentOnTimeout()  // Always calls the latest version
                        }
                        
                        Image(painter = painterResource(R.drawable.logo), contentDescription = null)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Keyboard hide/show

                    @Composable
                    fun keyboardShownState(): State<Boolean> {
                        val isImeVisible = WindowInsets.ime.getBottom(LocalDensity.current) > 0
                        return rememberUpdatedState(isImeVisible)
                    }

                    val localFocusManager = LocalFocusManager.current
                    if (!keyboardShownState().value) localFocusManager.clearFocus()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    private const val SplashWaitTime: Long = 2000

                    @Composable
                    fun LandingScreen(onTimeout: () -> Unit, modifier: Modifier = Modifier) {
                        Box (modifier = modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            val currentOnTimeOut by rememberUpdatedState(newValue = onTimeout)

                            LaunchedEffect (Unit) {
                                delay(SplashWaitTime)
                                currentOnTimeOut()
                            }

                            Image (painterResource (id = R.drawable.ic_crane_drawer), contentDescription = null)
                        }
                    }

                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun TimerComposable(onTimeChanged: (Int) -> Unit) {
                        // Remember the latest onTimeChanged callback without restarting the effect
                        val updatedOnTimeChanged by rememberUpdatedState(onTimeChanged)

                    // Launch a coroutine that updates the time every second
                        LaunchedEffect(Unit) {
                            var time = 0
                            while (true) {
                                delay(1000L)
                                time++
                                updatedOnTimeChanged(time)  // Call the latest onTimeChanged callback with the new time
                            }
                        }
                    }
                    @Composable
                    fun TimerScreen() {
                        var displayedTime by remember { mutableStateOf(0) }
                        // Update displayedTime every second
                        TimerComposable(onTimeChanged = { newTime ->
                            displayedTime = newTime
                        })
                        Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                            Text(text = displayedTime)
                            Spacer(modifier = Modifier.height(16.dp))
                            Button(onClick = {
                                // Change the behavior of onTimeChanged dynamically
                                TimerComposable(onTimeChanged = { newTime ->
                                    displayedTime = newTime * 2 // Example: Display double the actual time
                                })
                            }) {
                                Text("Double the Time")
                            }
                        }
                    }

                    @Composable
                    fun UserScreen(viewModel: UserViewModel = viewModel()) {
                        val viewState by viewModel.viewState.collectAsState()

                        // Capture the latest errorMessage using rememberUpdatedState
                        val currentErrorMessage by rememberUpdatedState(viewState.errorMessage)

                        LaunchedEffect(Unit) {
                            if (currentErrorMessage != null) {
                                // Show Snackbar with the latest error message
                                SnackbarHostState().showSnackbar(currentErrorMessage!!)
                            }
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf allows you to derive a new state from existing ones, and only recomputes when necessary — no redundant updates. filteredList will only update when query changes — not on every recomposition.  derivedStateOf inside 𝐫𝐞𝐦𝐞𝐦𝐛𝐞𝐫{} to ensure Compose can properly cache the result.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Approach not large lists filter
                    @Composable
                    fun SearchList(
                        items: List<String>,
                        query: String
                    ) {
                        val filteredItems by remember(items, query) {
                            derivedStateOf {
                                if (query.isBlank()) items
                                else items.filter { it.contains(query, ignoreCase = true) }
                            }
                        }

                        LazyColumn {
                            items(filteredItems) { item ->
                                Text(item, modifier = Modifier.padding(8.dp))
                            }
                        }
                    }
                    
                    //Search in ViewModel
                    class SearchViewModel : ViewModel() {
                        private val _items = MutableStateFlow(listOf("Apple", "Banana", "Orange"))
                        private val _query = MutableStateFlow("")

                        val filteredItems = combine(_items, _query) { items, query ->
                            if (query.isBlank()) items
                            else items.filter { it.contains(query, ignoreCase = true) }
                        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(), emptyList())

                        fun onQueryChange(newQuery: String) {
                            _query.value = newQuery
                        }
                    }
                    
                    @Composable
                    fun SearchScreen(viewModel: SearchViewModel = viewModel()) {
                        val items by viewModel.filteredItems.collectAsState()

                        Column {
                            TextField(
                                value = "",
                                onValueChange = { viewModel.onQueryChange(it) },
                                placeholder = { Text("Search...") }
                            )
                            LazyColumn {
                                items(items, key = { it }) { Text(it) }
                            }
                        }
                    }
                    
                    //1. Best Way Debounce using Filter
                    class SearchViewModel : ViewModel() {

                        private val _query = MutableStateFlow("")
                        private val _items = MutableStateFlow(
                            listOf("Apple", "Banana", "Orange", "Mango", "Grapes", "Pineapple")
                        )

                        // Exposed filtered items with debounce
                        val filteredItems: StateFlow<List<String>> = _query
                            .debounce(300) // wait for user to pause typing
                            .combine(_items) { query, items ->
                                if (query.isBlank()) items
                                else items.filter { it.contains(query, ignoreCase = true) }
                            }
                            .stateIn(
                                scope = viewModelScope,
                                started = SharingStarted.WhileSubscribed(5_000),
                                initialValue = emptyList()
                            )

                        fun onQueryChange(newQuery: String) {
                            _query.value = newQuery
                        }
                    }
                    
                    @Composable
                    fun SearchScreen(viewModel: SearchViewModel = viewModel()) {
                        val items by viewModel.filteredItems.collectAsState()

                        Column {
                            var query by remember { mutableStateOf("") }

                            TextField(
                                value = query,
                                onValueChange = {
                                    query = it
                                    viewModel.onQueryChange(it) // delegate to ViewModel
                                },
                                placeholder = { Text("Search...") },
                                modifier = Modifier.fillMaxWidth()
                            )

                            LazyColumn {
                                items(items, key = { it }) { item ->
                                    Text(item, modifier = Modifier.padding(8.dp))
                                }
                            }
                        }
                    }
                    
                    //2. Best Way Filter
                    val fruits = listOf()

                    @Composable
                    private fun rememberFruitList(searchText: String): State<List<String>>{
                        val updateQuery by rememberUpdatedState(newValue = searchText)

                        return remember {
                            derivedStateOf {
                                fruits.filter { it.contains(searchText.trim(), ignoreCase = true) }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/percy-g2/kmp_utxo

                    @OptIn(ExperimentalFoundationApi::class)
                    @Composable
                    fun CryptoList(cryptoViewModel: CryptoViewModel) {
                        val lifecycleOwner = LocalLifecycleOwner.current
                        val trades by cryptoViewModel.trades.collectAsState()
                        val tickerDataMap by cryptoViewModel.filteredTickerDataMap.collectAsState()
                        val isLoading by cryptoViewModel.isLoading.collectAsState()
                        val listState = rememberLazyListState()
                        val tradingPairs = cryptoViewModel.tradingPairs.collectAsState()
                        val settingsStore by store.updates.collectAsState(Settings())
                        val coroutineScope = rememberCoroutineScope()

                        val visibleSymbols by remember {
                            derivedStateOf {
                                val layoutInfo = listState.layoutInfo
                                val items = tickerDataMap.values.toList()

                                val prefetchBuffer = 10
                                val visibleIndices = layoutInfo.visibleItemsInfo.map { it.index }

                                val startIndex = visibleIndices.minOrNull()?.minus(prefetchBuffer)?.coerceAtLeast(0) ?: 0
                                val endIndex = visibleIndices.maxOrNull()?.plus(prefetchBuffer)?.coerceAtMost(items.lastIndex)
                                        ?: items.lastIndex

                                (startIndex..endIndex).mapNotNull { index ->
                                    items.getOrNull(index)?.symbol
                                }
                            }
                        }

                        LaunchedEffect(visibleSymbols) {
                            if (visibleSymbols.isNotEmpty()) {

                            }
                        }

                        // Lifecycle observer to reconnect when the app comes back to foreground
                        DisposableEffect(lifecycleOwner) {
                            val observer = LifecycleEventObserver { _, event ->
                                if (event == Lifecycle.Event.ON_RESUME) {
                                    cryptoViewModel.reconnect()
                                    if (visibleSymbols.isNotEmpty()) {

                                    }
                                }
                            }
                            lifecycleOwner.lifecycle.addObserver(observer)
                            onDispose { lifecycleOwner.lifecycle.removeObserver(observer) }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    @Composable
                    fun CountdownTimer(duration: Int, onFinish: () → Unit)
                    {
                        val currentOnFinish by rememberUpdatedState(onFinish)
                        var timeLeft by remember { mutableState0f(duration) }

                        LaunchedEffect (duration) {
                            while (timeLeft > 0) {
                                delay (1000)
                                timeLeft--
                            }
                            currentOnFinish() // Side effect: Trigger callback
                        }
                        Text("Time left: $timeLeft seconds")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    @Composable
                    fun CircleCalculator() {
                        var radius by rememberUpdatedState(0f)
                        var color by rememberUpdatedState(Color.Red)

                        val area = remember(radius) { calculateArea(radius) }
                        val circumference = remember(radius) { calculateCircumference(radius) }

                        Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
                            TextField(
                                value = radius.toString(),
                                onValueChange = { radius = it.toFloatOrNull() ?: 0f },
                                label = { Text("Enter radius") },
                                keyboardOptions = KeyboardOptions.Default.copy(
                                    keyboardType = KeyboardType.Number
                                ),
                                modifier = Modifier.fillMaxWidth()
                            )
                            Spacer(modifier = Modifier.height(16.dp))
                            ColorPicker(
                                colors = listOf(Color.Red, Color.Blue, Color.Green, Color.Yellow),
                                selectedColor = color,
                                onColorSelected = { color = it }
                            )
                            Spacer(modifier = Modifier.height(16.dp))
                            Text("Area: ${area} units²")
                            Text("Circumference: ${circumference} units")
                            Spacer(modifier = Modifier.weight(1f))
                            Box(
                                modifier = Modifier
                                    .background(color, CircleShape)
                                    .fillMaxSize()
                                    .aspectRatio(1f)
                            )
                        }
                    }

                    fun calculateArea(radius: Float): Float {
                        return (Math.PI * radius * radius).toFloat()
                    }

                    fun calculateCircumference(radius: Float): Float {
                        return (2 * Math.PI * radius).toFloat()
                    }

                    @Composable
                    fun ColorPicker(
                        colors: List<Color>,
                        selectedColor: Color,
                        onColorSelected: (Color) -> Unit
                    ) {
                        Row(modifier = Modifier.horizontalScroll(rememberScrollState())) {
                            colors.forEach { color ->
                                Box(
                                    modifier = Modifier
                                        .background(color, CircleShape)
                                        .padding(16.dp)
                                        .size(50.dp)
                                        .clickable(onClick = { onColorSelected(color) })
                                        .border(
                                            width = 2.dp,
                                            color = if (color == selectedColor) Color.Black else Color.Transparent,
                                            shape = CircleShape
                                        )
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If the text updates frequently, rememberUpdatedState helps avoid triggering unnecessary recompositions:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun LiveText(text: String) {
                        val currentText by rememberUpdatedState(text)
                        Text(currentText)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Wrong
                    var count = mutableStateOf(0)

                    //Good
                    var count by remember { mutableStateOf(0) }
                    """.trimIndent()
            )

            SpanText(
                text = "What Not To Do",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "Why we need SideEffect?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, UI is built using pure functions — they take input (state) and return UI (Composable functions). This makes Compose powerful and predictable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "But not everything in an app is pure. Sometimes, we need to interact with things outside the Composable world.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Don’t use rememberUpdatedState as a replacement for triggering state changes in the UI. It's meant for holding and using values across recompositions without forcing recomposition itself.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How do you safely create a coroutine scope within composable functions?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, using rememberCoroutineScope is the recommended approach to safely create and manage a coroutine scope within a composable function. This ensures that the coroutine scope is tied to the composition, preventing potential memory leaks and improper resource usage.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How rememberUpdatedState works?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creates a single object and automatically updates its value at each recomposition, ensuring that the most recent value always appears in the lambdas.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It wraps the parameter with remembered MutableState and updates its value on every recomposition. By doing this, the LaunchedEffect is referencing the same instance of the MutableState object instead of the text parameter. So even after recomposition, the LaunchedEffect still references the correct instance, which changes only its internal value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}

//https://github.com/stoyan-vuchev/squircle-shape.git